import React, { useState } from 'react';
import { Box, Container, Typography, Paper, Grid } from '@mui/material';
import { motion } from 'framer-motion';
import { EventRepeat, Block, CreditCard, QueryBuilder, Autorenew } from '@mui/icons-material';

const policySections = [
  {
    id: "return-window",
    number: "1",
    icon: <EventRepeat />,
    title: "Return Window",
    content: "We offer a comprehensive 30-day return window from the date of purchase. To be eligible for a return, the software key must be unactivated and in the same condition that you received it. If 30 days have gone by since your purchase, unfortunately we can’t offer you a refund or exchange."
  },
  {
    id: "exceptions",
    number: "2",
    icon: <Block />,
    title: "Exceptions / Non-returnable Items",
    content: "Certain types of items cannot be returned. We cannot accept returns for activated software licenses, digital downloads that have already been accessed or utilized, or any customized services. If you encounter an issue with activation, please contact our support team instead of initiating a return."
  },
  {
    id: "refund-process",
    number: "3",
    icon: <CreditCard />,
    title: "Refund Process",
    content: "Once we receive your return request, we will inspect the status of the license key and notify you of the approval or rejection of your refund. If you are approved, then your refund will be processed, and a credit will automatically be applied to your credit card or original method of payment, within a certain amount of days."
  },
  {
    id: "late-refunds",
    number: "4",
    icon: <QueryBuilder />,
    title: "Late or Missing Refunds",
    content: "If you haven’t received a refund yet, first check your bank account again. Then contact your credit card company, it may take some time before your refund is officially posted. Next contact your bank. There is often some processing time before a refund is posted. If you’ve done all of this and you still have not received your refund yet, please contact us."
  },
  {
    id: "exchanges",
    number: "5",
    icon: <Autorenew />,
    title: "Exchanges",
    content: "We only replace items if they are defective or damaged (e.g., if you receive an invalid key). If you need to exchange it for the same item, send us an email at arinfotechsolution11@gmail.com with your order details."
  }
];

const ReturnsRefund = () => {
  const [activeSection, setActiveSection] = useState(policySections[0].id);

  const scrollToSection = (id) => {
    setActiveSection(id);
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#F1F5F9', pb: 10 }}>
      {/* Banner Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #0F172A 0%, #0369A1 100%)', // Slightly different blue for variety
          color: '#FFF',
          py: { xs: 8, md: 12 },
          position: 'relative',
          overflow: 'hidden',
          textAlign: 'center'
        }}
      >
        <motion.div
          animate={{ scale: [1, 1.1, 1] }}
          transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'radial-gradient(circle at 80% 20%, rgba(56,189,248,0.1) 0%, rgba(0,0,0,0) 50%)',
            pointerEvents: 'none'
          }}
        />
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          <motion.div initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.6 }}>
            <Typography variant="h2" sx={{ fontWeight: 800, mb: 2, fontSize: { xs: '2.5rem', md: '3.5rem' } }}>
              Returns & Refunds
            </Typography>
            <Typography variant="h6" sx={{ opacity: 0.8, fontWeight: 400 }}>
              A transparent guide to our returns and refund procedures.
            </Typography>
          </motion.div>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ mt: { xs: -4, md: -6 }, position: 'relative', zIndex: 2 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={4} lg={3}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 3,
                boxShadow: '0 10px 30px -10px rgba(0,0,0,0.05)',
                position: { md: 'sticky' },
                top: { md: 100 },
                backgroundColor: 'rgba(255,255,255,0.9)',
                backdropFilter: 'blur(10px)'
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#1E293B' }}>
                Table of Contents
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {policySections.map((section) => (
                  <Box
                    key={`toc-${section.id}`}
                    onClick={() => scrollToSection(section.id)}
                    sx={{
                      cursor: 'pointer',
                      py: 1,
                      px: 2,
                      borderRadius: 1,
                      transition: 'all 0.2s',
                      backgroundColor: activeSection === section.id ? 'rgba(3, 105, 161, 0.08)' : 'transparent',
                      color: activeSection === section.id ? '#0369A1' : '#64748B',
                      borderLeft: activeSection === section.id ? '3px solid #0369A1' : '3px solid transparent',
                      fontWeight: activeSection === section.id ? 600 : 400,
                      '&:hover': {
                        backgroundColor: 'rgba(3, 105, 161, 0.04)',
                        color: '#0369A1'
                      }
                    }}
                  >
                    <Typography variant="body2">{section.number}. {section.title}</Typography>
                  </Box>
                ))}
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} md={8} lg={9}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {policySections.map((section, index) => (
                <motion.div
                  key={`content-${section.id}`}
                  initial={{ y: 50, opacity: 0 }}
                  whileInView={{ y: 0, opacity: 1 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Paper
                    id={section.id}
                    elevation={2}
                    sx={{
                      p: { xs: 3, md: 5 },
                      borderRadius: 3,
                      backgroundColor: '#FFFFFF',
                      boxShadow: '0 10px 25px -5px rgba(0,0,0,0.1)',
                      border: '1px solid rgba(0,0,0,0.05)',
                      transition: 'transform 0.3s, box-shadow 0.3s',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 10px 30px -10px rgba(0,0,0,0.1)'
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                      <Box sx={{
                        backgroundColor: '#0369A1',
                        color: '#FFF',
                        width: 44,
                        height: 44,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '10%',
                        fontWeight: 700,
                        fontSize: '1.25rem',
                        flexShrink: 0
                      }}>
                        {section.number}
                      </Box>
                      <Box sx={{ color: '#0369A1', display: 'flex', opacity: 0.8 }}>
                        {section.icon}
                      </Box>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: '#0F172A' }}>
                        {section.title}
                      </Typography>
                    </Box>
                    <Typography variant="body1" sx={{ color: '#475569', lineHeight: 1.8 }}>
                      {section.content}
                    </Typography>
                  </Paper>
                </motion.div>
              ))}
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default ReturnsRefund;
